import { useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import Link from 'next/link';
import styled from 'styled-components';

import Button from '@r1-frontend/ui-react/components/buttons/button';
import { ButtonGroup } from '@r1-frontend/ui-react/components/buttons/buttonGroup/ButtonGroup';
import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import DefaultPopup from '@r1-frontend/ui-react/components/popups/defaultPopup';
import ControlElement from '@r1-frontend/ui-react/components/typography/controlElement';
import { H3 } from '@r1-frontend/ui-react/components/typography/heading';
import { Paragraph4 } from '@r1-frontend/ui-react/components/typography/paragraph';
import TextWrapper from '@r1-frontend/ui-react/components/typography/TextWrapper';
import WebpImage from '@r1-frontend/ui-react/components/WebpImage';
import { FullWidthListContainer, IndentContainer, ListContainer, NonStaticContainer } from '@r1-frontend/ui-react/experimental/containers';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { FONTS } from '@r1-frontend/ui-react/tokens/fonts';
import { progressiveBackground } from '@r1-frontend/shared/helpers/progressiveBackground';
import { useMatchMediaByWidth } from '@r1-frontend/shared/hooks/useMatchMedia';

import { toggleIsShowSpecOffer } from '~/src/features/payments-abn/store/actionCreators';
import { specialOfferAnalytics } from '~/src/features/specialOffersContainer/analytics';
import { viewOffer } from '~/src/features/specialOffersContainer/store/actions/thunk';
import { selectNewOrViewedOffers } from '~/src/features/specialOffersContainer/store/selectors';
import { TState } from '~/src/store';
import SpecOfferImage from '~/static/images/payments-abn/stopper/spec-offer.png';
import SpecOfferImageWebp from '~/static/images/payments-abn/stopper/spec-offer.webp';
import SpecOfferWithLabelImage from '~/static/images/payments-abn/stopper/spec-offer-with-label.png';
import SpecOfferWithLabelImageWebp from '~/static/images/payments-abn/stopper/spec-offer-with-label.webp';
import SpecOfferWithLabelMobileImage from '~/static/images/payments-abn/stopper/spec-offer-with-label-mobile.png';
import SpecOfferWithLabelMobileImageWebp from '~/static/images/payments-abn/stopper/spec-offer-with-label-mobile.webp';
import StopperDesktopBackground from '~/static/images/payments-abn/stopper/stopper-desktop-bg.png';
import StopperDesktopBackgroundWebp from '~/static/images/payments-abn/stopper/stopper-desktop-bg.webp';
import StopperMobileBackground from '~/static/images/payments-abn/stopper/stopper-mobile-bg.png';
import StopperMobileBackgroundWebp from '~/static/images/payments-abn/stopper/stopper-mobile-bg.webp';

import { useLabelAbnTest } from './useLabelAbnTest';

interface IProps {
    buttonText: string,
    onConnect: () => void,
    onClose?: () => void,
    className?: string,
}

const SpecialOfferPopup = ({ buttonText, onConnect, onClose, className }: IProps): JSX.Element | null => {
    const isDesktop = useMatchMediaByWidth(wideBreakpoints.mobile, true);

    const dispatch = useDispatch();

    const { stopperData: { stopper, hasOtherOffers }, isShowSpecOffer } = useSelector((state: TState) => ({
        stopperData: selectNewOrViewedOffers(state),
        isShowSpecOffer: state.payment.isShowSpecOffer,
    }), shallowEqual);

    const isImageWithLabel = useLabelAbnTest();

    const { image, webpImage } = useMemo(() => {
        if (isImageWithLabel) {
            return isDesktop
                ? {
                    image: SpecOfferWithLabelImage,
                    webpImage: SpecOfferWithLabelImageWebp,
                }
                : {
                    image: SpecOfferWithLabelMobileImage,
                    webpImage: SpecOfferWithLabelMobileImageWebp,
                };
        }

        return {
            image: SpecOfferImage,
            webpImage: SpecOfferImageWebp,
        };
    }, [isDesktop, isImageWithLabel]);

    if (!stopper) {
        return null;
    }

    const { hasInstantConnect, shortDescription, title } = stopper;

    const showOfferDetails = () => {
        specialOfferAnalytics.showPromoDetails(stopper);
        dispatch(toggleIsShowSpecOffer(false));
        dispatch(viewOffer(stopper, false));
    };

    const handleShowAllOffers = () => {
        specialOfferAnalytics.showAllPromo(stopper);
        dispatch(toggleIsShowSpecOffer(false));
    };

    const onCloseStopper = () => {
        dispatch(toggleIsShowSpecOffer(false));
        onClose && onClose();
    };

    return (
        <DefaultPopup isOpen={isShowSpecOffer} onClose={onCloseStopper} className={className}>
            <IndentContainer $desktop={[32]} $mobile={[24]}>
                <FullWidthListContainer $direction={'column'} $wrap={'nowrap'} $desktop={24} $mobile={16}>
                    <ListContainer $direction={'column'} $size={0}>
                        <TextWrapper $lastIndent={false}>
                            <H3>{title}</H3>
                            <Paragraph4 $color={COLORS.TextInfo} dangerouslySetInnerHTML={{ __html: shortDescription }}/>
                        </TextWrapper>
                        {hasInstantConnect && (
                            <ControlElement
                                $customFont={FONTS[isDesktop ? 'MediumS' : 'MediumXS']}
                                onClick={showOfferDetails}
                            >
                                Подробнее
                            </ControlElement>
                        )}
                    </ListContainer>

                    <ButtonGroup>
                        <Button bType={'action'} size={'large'} onClick={onConnect}>
                            {hasInstantConnect ? buttonText : 'Подробнее'}
                        </Button>
                        {hasOtherOffers && (
                            <Link href={'/lk/promo'} passHref>
                                <Button bType={'secondary'} size={'large'} onClick={handleShowAllOffers}>
                                    Другие акции
                                </Button>
                            </Link>
                        )}
                    </ButtonGroup>
                </FullWidthListContainer>
            </IndentContainer>

            <NonStaticContainer
                $desktop={{
                    $position: 'absolute',
                    $top: 44,
                    $right: 69,
                }}
                $mobile={{
                    $position: 'absolute',
                    $top: 50,
                    $right: isImageWithLabel ? 0 : -39,
                    $zIndex: -1,
                }}
            >
                <WebpImage urlWebp={webpImage}>
                    <img src={image} alt='Изображение подарка'/>
                </WebpImage>
            </NonStaticContainer>
        </DefaultPopup>
    );
};

export default styled(SpecialOfferPopup)`
    background: unset;
    background-color: ${COLORS.BgMain};
    ${progressiveBackground({ src: StopperDesktopBackground, webpSrc: StopperDesktopBackgroundWebp })};
    background-repeat: no-repeat;
    background-size: cover;
    overflow: hidden;

    ${TextWrapper} {
        max-width: 336px;
    }

    & > ${NonStaticContainer} {
        width: 170px;
        height: 148px;

        img,
        picture {
            width: 100%;
            height: 100%;
            object-position: center;
            object-fit: contain;
        }
    }

    @media (max-width: ${wideBreakpoints.mobile}) {
        ${progressiveBackground({ src: StopperMobileBackground, webpSrc: StopperMobileBackgroundWebp })};

        ${TextWrapper} {
            max-width: calc(100% - 36px);
        }

        & > ${NonStaticContainer} {
            width: 105px;
            height: 91px;
        }
    }
`;
